import { Typography, TextSize } from '@shared/index';
import { formatNum } from '@app/utils/math';
import { TableThead, TableTr, TableTh, Table, TableBody, TableTd } from '@widgets/table';
import { useMemo } from 'react';
import { daysUntilEndOfQuarter } from '@app/utils/date';
import { getQuarterCurrent } from '@app/utils/quarters';
import { getCurrentDate } from '@shared/utils/getCurrentDate';
import { useGetTeamQuarterlyBonusResults } from '@entities/managers-perfomance';

export const QuarterlyTeamBonusResults = () => {
  const { year } = getCurrentDate();
  const quarter = getQuarterCurrent();

  const { data: teamQuarterlyBonusResults = [] } = useGetTeamQuarterlyBonusResults({
    year,
    quarter,
  });

  const { managersData, maxResult } = useMemo(() => {
    if (!teamQuarterlyBonusResults.length) {
      return { managersData: [], maxResult: 1000 };
    }

    const mapped = teamQuarterlyBonusResults.map(result => ({
      id: result.manager_id,
      name: result.manager_name,
      result: result.hashrate,
    }));

    const maxHashrate = Math.max(...mapped.map(hashrate => hashrate.result), 1000);

    return {
      managersData: mapped,
      maxResult: maxHashrate,
    };
  }, [teamQuarterlyBonusResults]);

  const daysLeft = daysUntilEndOfQuarter();

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <Typography size={TextSize.size24} className="font-semibold" title="Результат за квартал" />
        <div className="flex items-center gap-2 bg-[#202020] rounded-md px-2 py-1">
          <Typography size={TextSize.size10} className="text-gray-400" text="Осталось, дн" />
          <Typography size={TextSize.size12} text={daysLeft} />
        </div>
      </div>
      <div className='h-[238px] overflow-y-auto'>
        <Table>
          <TableThead className="text-white/40 [&_div]:justify-start">
            <TableTr withoutUnderline>
              <TableTh title="Менеджер" className="w-[25%]" />
              <TableTh title="Результат, Ph" className="w-[75%]" />
              <TableTh title="" className="w-[15%]" />
            </TableTr>
          </TableThead>
          <TableBody>
            {managersData.map(({ id, name, result }) => {
              const progress = (result / maxResult) * 100;
              const isFullGreen = progress >= 100;

              const greyHalf = Math.min(progress, 50);
              const greenHalf = Math.max(progress - 50, 0);

              const greyWidth = Math.min(greyHalf, 50);
              const greenWidth = Math.min(greenHalf, 50);

              return (
                <TableTr key={id} withoutUnderline>
                  <TableTd
                    title={
                      <Typography
                        size={TextSize.size12}
                        className={isFullGreen ? 'text-white font-semibold' : 'text-white/60'}
                        text={name}
                      />
                    }
                  />
                  <TableTd
                    title={
                      <div className="flex-1 relative h-[8px] w-full" style={{ width: '100%' }}>
                        <div
                          className="absolute inset-0 bg-transparent rounded-full overflow-visible"
                          style={{ width: '100%' }}
                        >
                          {isFullGreen ? (
                            <div
                              className="absolute inset-0 h-full rounded-full"
                              style={{ backgroundColor: '#7AC08B', width: '100%' }}
                            />
                          ) : (
                            <>
                              {greyWidth > 0 && (
                                <div
                                  className="absolute left-0 h-full rounded-l-full"
                                  style={{
                                    width: `${greyWidth}%`,
                                    backgroundColor: '#6E6E6E',
                                  }}
                                />
                              )}
                              <div
                                className="absolute w-px bg-[#A6A6A6] z-10"
                                style={{
                                  left: 'calc(50% - 0.5px)',
                                  top: '50%',
                                  height: '21px',
                                  transform: 'translateY(-50%)',
                                }}
                              />
                              {greenWidth > 0 && (
                                <div
                                  className="absolute left-1/2 h-full rounded-r-full"
                                  style={{
                                    width: `${greenWidth}%`,
                                    backgroundColor: '#7AC08B',
                                  }}
                                />
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    }
                  />
                  <TableTd
                    title={
                      <Typography
                        size={TextSize.size12}
                        className={isFullGreen ? 'text-green-200' : 'text-white/60'}
                        text={formatNum(result)}
                      />
                    }
                  />
                </TableTr>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
